import { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { X, CheckSquare, Square, Clock, ClipboardList } from "lucide-react";
import type { ChecklistCaseDetail, TemplateItem } from "./types";
import { ScoreRing } from "./Charts";

type Props = {
  detail: ChecklistCaseDetail;
  onClose: () => void;
};

export function CaseViewer({ detail, onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const groupedItems = useMemo(() => {
    if (!detail.template_items) return {} as Record<string, TemplateItem[]>;
    const grouped: Record<string, TemplateItem[]> = {};
    for (const item of detail.template_items) {
      const cat = item.category?.trim() || "Other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    }
    for (const cat in grouped) {
      grouped[cat].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return grouped;
  }, [detail]);

  const checkedStats = useMemo(() => {
    const items = detail.checklist_data?.items || {};
    const total = detail.template_items?.length || 0;
    const checked = Object.values(items).filter((v) => v?.checked).length;
    return { checked, total };
  }, [detail]);

  const sanitized = useMemo(() => {
    if (!detail.content?.data) return "";
    return DOMPurify.sanitize(detail.content.data, {
      ADD_ATTR: ["target"],
    });
  }, [detail]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            <ScoreRing score={detail.score} size={64} label="Quality score" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Case
              </div>
              <div className="text-lg font-semibold text-slate-900 truncate">
                {detail.case_number || `Case ${detail.id.slice(0, 8)}`}
              </div>
              <div className="text-sm text-slate-500">
                <span>{detail.officer_name}</span>
                {detail.badge_id && <span className="ml-1">#{detail.badge_id}</span>}
                <span className="mx-2 text-slate-300">·</span>
                <span>
                  {new Date(detail.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                {detail.template_name && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1">
                      <ClipboardList size={14} />
                      {detail.template_name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 md:grid-cols-[1.35fr_1fr] gap-0 flex-1 overflow-hidden">
          {/* Report */}
          <div className="overflow-y-auto px-8 py-6 border-r border-slate-200 bg-white">
            <div
              className="prose prose-slate max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2 prose-p:my-2 prose-p:leading-relaxed prose-p:text-slate-700"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
          </div>

          {/* Checklist */}
          <div className="overflow-y-auto bg-slate-50">
            <div className="px-6 py-5 sticky top-0 bg-slate-50 z-10 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-700 font-semibold">
                  <ClipboardList size={16} className="text-blue-600" />
                  Checklist
                </div>
                <div className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-800">
                    {checkedStats.checked}
                  </span>
                  <span className="mx-1">/</span>
                  {checkedStats.total} completed
                </div>
              </div>
              {checkedStats.total > 0 && (
                <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-700"
                    style={{ width: `${(checkedStats.checked / checkedStats.total) * 100}%` }}
                  />
                </div>
              )}
              {detail?.checklist_completed_at && (
                <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                  <Clock size={12} />
                  Completed{" "}
                  {new Date(detail.checklist_completed_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-5">
              {detail.template_items && (
                <div className="space-y-5">
                  {Object.entries(groupedItems).map(([category, items]) => (
                    <div key={category}>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                        {category}
                      </div>
                      <div className="space-y-1.5">
                        {items.map((item) => {
                          const state = detail.checklist_data?.items?.[item.id];
                          const checked = !!state?.checked;
                          return (
                            <div
                              key={item.id}
                              className={`flex items-start gap-2.5 rounded-md px-3 py-2 transition-colors ${
                                checked
                                  ? "bg-white border border-blue-100"
                                  : "bg-slate-100/60 border border-transparent"
                              }`}
                            >
                              {checked ? (
                                <CheckSquare className="text-blue-500 shrink-0 mt-0.5" size={16} />
                              ) : (
                                <Square className="text-slate-300 shrink-0 mt-0.5" size={16} />
                              )}
                              <div className="min-w-0 flex-1">
                                <div
                                  className={`text-sm ${
                                    checked ? "text-slate-800" : "text-slate-500"
                                  }`}
                                >
                                  {item.text}
                                  {item.required && (
                                    <span className="ml-1 text-[10px] font-semibold text-orange-600 uppercase">
                                      req
                                    </span>
                                  )}
                                </div>
                                {state?.additionalNotes && (
                                  <div className="mt-1.5 text-xs text-slate-700 bg-amber-50 border-l-2 border-amber-400 px-2.5 py-1.5 rounded-sm whitespace-pre-wrap">
                                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5">
                                      Note
                                    </div>
                                    {state.additionalNotes}
                                  </div>
                                )}
                                {checked && state?.checked_at && (
                                  <div className="text-[10px] text-slate-400 mt-0.5">
                                    {new Date(state.checked_at).toLocaleTimeString("en-US", {
                                      hour: "numeric",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

